import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import type { PublicBookingOptions, PublicPage, PublicSiteInfo } from '@barbeiropro/contracts';
import { PublicAssistantWidget } from '@/features/ai/public-assistant-widget';
import { HttpError } from '@/lib/api';
import { BookingPage } from './booking-page';
import { ManagePage } from './manage-page';
import { pub } from './pub-api';
import { PageView, SiteShell } from './site-view';
import { displayStyle } from './theme';

export const siteKey = (slug: string) => ['site-public', slug, 'info'] as const;
export const bookingOptionsKey = (slug: string) => ['site-public', slug, 'booking-options'] as const;

function setMeta(name: string, content: string) {
  let m = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!m) {
    m = Object.assign(document.createElement('meta'), { name });
    document.head.appendChild(m);
  }
  m.content = content;
}

export function useDocumentMeta(title: string | undefined, description?: string) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('description', description);
  }, [title, description]);
}

function Centered({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-[#f6f1e7] px-6 text-center text-[#1f1b16]">
      <h1 className="text-5xl" style={{ fontFamily: "'Big Shoulders Display',sans-serif", fontWeight: 800, textTransform: 'uppercase' }}>{title}</h1>
      {children}
    </div>
  );
}

const DEFAULT_THEME: PublicSiteInfo['theme'] = { primary: '#1f1b16', accent: '#b0823c', background: '#f6f1e7', fontPair: 'classic' };

function PagePart({ slug, site, pageSlug }: { slug: string; site: PublicSiteInfo; pageSlug: string }) {
  const q = useQuery({
    queryKey: ['site-public', slug, 'page', pageSlug],
    queryFn: () => pub<PublicPage>('GET', `/public/${slug}/pages/${pageSlug || '_home'}`),
    retry: false,
  });
  const org = site.organization.name;
  useDocumentMeta(q.data ? q.data.seo.title || (pageSlug ? `${q.data.title} · ${org}` : org) : org, q.data?.seo.description);
  if (q.isPending) return <div role="status" className="px-6 py-40 text-center" style={{ color: 'var(--s-muted)' }}>Carregando…</div>;
  if (q.isError)
    return (
      <div className="px-6 py-40 text-center">
        <p className="text-5xl" style={displayStyle}>Página não encontrada</p>
        <Link to={`/b/${site.organization.slug}`} className="mt-6 inline-block font-semibold underline underline-offset-4">Voltar ao início</Link>
      </div>
    );
  return <PageView page={q.data} />;
}

/** /b/:slug/* — site público sem login. */
export function PublicSiteRoot() {
  const { slug = '', '*': rest = '' } = useParams();
  const parts = rest.split('/').filter(Boolean);
  const first = parts[0]?.toLowerCase() ?? '';
  const isBooking = first === 'agendar';
  const isManage = first === 'agendamento';

  const site = useQuery({ queryKey: siteKey(slug), queryFn: () => pub<PublicSiteInfo>('GET', `/public/${slug}/site`), retry: false });
  // agendamento funciona mesmo sem site publicado: usa as opções para montar o cabeçalho
  const fallback = useQuery({
    queryKey: bookingOptionsKey(slug),
    queryFn: () => pub<PublicBookingOptions>('GET', `/public/${slug}/booking-options`),
    enabled: (isBooking || isManage) && site.isError,
    retry: false,
  });

  if (site.isPending) return <Centered title="Carregando…" />;

  let info: PublicSiteInfo | null = site.data ?? null;
  if (!info && (isBooking || isManage)) {
    if (fallback.isPending) return <Centered title="Carregando…" />;
    if (fallback.data) {
      info = {
        organization: { name: fallback.data.organization.name, slug, phone: null, whatsapp: null, email: null, instagram: null, facebook: null, tiktok: null, logoUrl: null, faviconUrl: null },
        theme: DEFAULT_THEME,
        nav: [],
        branches: [],
      };
    }
  }
  if (!info) {
    const notFound = site.error instanceof HttpError && site.error.status === 404;
    return (
      <Centered title={notFound ? 'Site não encontrado' : 'Não foi possível carregar'}>
        <p>{notFound ? 'Este endereço não existe ou o site ainda não foi publicado.' : 'Verifique a conexão e tente de novo.'}</p>
        {!notFound && (
          <button className="font-semibold underline" onClick={() => site.refetch()}>
            Tentar de novo
          </button>
        )}
      </Centered>
    );
  }

  return (
    <SiteShell slug={slug} site={info} pageSlug={isBooking || isManage ? '__' : first}>
      {isBooking ? <BookingPage slug={slug} orgName={info.organization.name} /> : isManage ? <ManagePage slug={slug} token={parts[1] ?? ''} /> : <PagePart slug={slug} site={info} pageSlug={first} />}
      <PublicAssistantWidget slug={slug} />
    </SiteShell>
  );
}
